import { Button, Flex, HStack, Text, useColorModeValue } from '@chakra-ui/react';
import { translate } from '../../i18n';

/**
 * Rows per page for client-side paginated tables.
 *
 * 50 keeps a page well under the ~1,000 Chakra cells the main thread can
 * style and commit without a visible stall (measured on the users table:
 * 4,000 rows × 9 cells blocked the thread for ~7.7 s in dev, the pagination
 * brought the same switch under 200 ms).
 */
export const DEFAULT_TABLE_PAGE_SIZE = 50;

interface TablePaginationProps {
    /** Zero-based current page. */
    pageIndex: number;
    pageCount: number;
    /** Rows across every page, shown as "(N total)". */
    totalCount: number;
    canPrevious: boolean;
    canNext: boolean;
    onPrevious: () => void;
    onNext: () => void;
}

/**
 * Footer for a CLIENT-SIDE paginated table (`getPaginationRowModel`).
 *
 * The dashboard already paginates its server-paged tables (support requests,
 * notifications, latest games, churn) with a "Page n of total (count total)"
 * label and Previous/Next buttons — see `dashboard/DESIGN.md`, Filtering &
 * Pagination. This is that same control for lists the API returns whole and
 * the table was rendering in one go. Renders nothing when everything fits on
 * one page, so small organizations see no new chrome.
 *
 * Usage:
 *   <TablePagination
 *     pageIndex={table.getState().pagination.pageIndex}
 *     pageCount={table.getPageCount()}
 *     totalCount={rows.length}
 *     canPrevious={table.getCanPreviousPage()}
 *     canNext={table.getCanNextPage()}
 *     onPrevious={table.previousPage}
 *     onNext={table.nextPage}
 *   />
 */
export default function TablePagination({
    pageIndex,
    pageCount,
    totalCount,
    canPrevious,
    canNext,
    onPrevious,
    onNext,
}: TablePaginationProps) {
    const textColor = useColorModeValue('secondaryGray.600', 'secondaryGray.500');

    if (pageCount <= 1) return null;

    const label = `${translate('game.page_of')} ${pageIndex + 1} ${translate('game.of')} ${pageCount} (${totalCount} ${translate('game.total')})`;

    return (
        <Flex
            role="navigation"
            aria-label={label}
            px={6}
            py={3}
            justify="flex-end"
            align="center"
            gap={4}
            flexWrap="wrap"
        >
            <Text fontSize="sm" color={textColor} data-testid="table-pagination-label">
                {label}
            </Text>
            <HStack spacing={2}>
                <Button size="sm" variant="outline" onClick={onPrevious} isDisabled={!canPrevious}>
                    {translate('common.previous')}
                </Button>
                <Button size="sm" variant="outline" onClick={onNext} isDisabled={!canNext}>
                    {translate('common.next')}
                </Button>
            </HStack>
        </Flex>
    );
}
